import { useState, useRef, useEffect } from 'react';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { useTranslation } from 'react-i18next';
import { User, LogOut, CreditCard, Key, Cloud as CloudIcon } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { signOut } from '@/lib/auth';

export default function AccountMenu() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, account } = useAuth();
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false);
    };
    const onEscape = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  if (!user) return null;

  const email = account?.email || user.email || '';
  const initial = (email[0] || '?').toUpperCase();

  const handleSignOut = async () => {
    setOpen(false);
    try { await signOut(); } catch {}
    navigate('/login');
  };

  return (
    <div className="relative shrink-0">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 pl-1 pr-2 min-h-[40px] rounded-full border border-gray-200 hover:border-indigo-300 hover:bg-gray-50 transition-colors"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
      >
        <span
          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold text-white"
          style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
        >
          {initial}
        </span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div ref={menuRef} role="menu" className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 z-50">
          <div className="px-3 py-2 border-b border-gray-100">
            <div className="text-xs text-gray-500">{t('common:nav.signed_in_as') || 'Signed in as'}</div>
            <div className="text-sm font-medium text-gray-900 truncate">{email}</div>
          </div>
          <MenuItem icon={User} onClick={() => { setOpen(false); navigate('/account'); }} label={t('common:nav.my_account') || 'My account'} />
          <MenuItem icon={CreditCard} onClick={() => { setOpen(false); navigate('/account/billing'); }} label={t('common:nav.billing') || 'Billing & plan'} />
          <MenuItem icon={CloudIcon} onClick={() => { setOpen(false); navigate('/cloud'); }} label={t('common:nav.cloud_documents') || 'Cloud documents'} />
          <MenuItem icon={Key} onClick={() => { setOpen(false); navigate('/activate'); }} label={t('common:nav.activate') || 'Activate code'} />
          <MenuItem icon={LogOut} onClick={handleSignOut} label={t('common:nav.sign_out') || 'Sign out'} />
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, onClick, label }) {
  return (
    <button type="button" onClick={onClick} role="menuitem"
      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors">
      <Icon size={14} className="text-gray-400" aria-hidden="true" />
      {label}
    </button>
  );
}
